import React, { useState, useEffect, useRef } from "react";
import { fetchWithAuth } from "../../utils/fetchWithAuth";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

export function AlertBell({ theme: t }) {
  const [alerts,  setAlerts]  = useState([]);
  const [open,    setOpen]    = useState(false);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);

  const unread = alerts.filter(a => !a.read).length;

  async function loadAlerts() {
    setLoading(true);
    try {
      const res = await fetchWithAuth(`${API}/api/alerts`);
      if (res.ok) setAlerts(await res.json());
    } catch {}
    setLoading(false);
  }

  useEffect(() => { loadAlerts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  async function markAllRead() {
    await fetchWithAuth(`${API}/api/alerts/read-all`, { method: "PUT" });
    setAlerts(a => a.map(x => ({ ...x, read: true })));
  }

  async function markRead(id) {
    await fetchWithAuth(`${API}/api/alerts/${id}/read`, { method: "PUT" });
    setAlerts(a => a.map(x => x.id === id ? { ...x, read: true } : x));
  }

  async function dismiss(id) {
    await fetchWithAuth(`${API}/api/alerts/${id}`, { method: "DELETE" });
    setAlerts(a => a.filter(x => x.id !== id));
  }

  const SEVERITY_COLOR = { high: "#DC2626", medium: "#f59e0b", low: "#64748b" };

  return (
    <div style={{ position: "relative" }} ref={panelRef}>
      <style>{`
        @keyframes bell-ring {
          0%   { transform: rotate(0deg); }
          10%  { transform: rotate(-15deg); }
          20%  { transform: rotate(15deg); }
          30%  { transform: rotate(-10deg); }
          40%  { transform: rotate(10deg); }
          50%  { transform: rotate(-5deg); }
          60%  { transform: rotate(5deg); }
          70%  { transform: rotate(0deg); }
          100% { transform: rotate(0deg); }
        }
        @keyframes bell-ring-gentle {
          0%   { transform: rotate(0deg); }
          20%  { transform: rotate(-4deg); }
          40%  { transform: rotate(4deg); }
          60%  { transform: rotate(0deg); }
          100% { transform: rotate(0deg); }
        }
        @keyframes badge-pulse {
          0%   { transform: scale(1); }
          50%  { transform: scale(1.3); }
          100% { transform: scale(1); }
        }
        @keyframes bell-glow {
          0%   { filter: drop-shadow(0 0 2px #ef4444); }
          50%  { filter: drop-shadow(0 0 8px #ef4444); }
          100% { filter: drop-shadow(0 0 2px #ef4444); }
        }
      `}</style>
      <button
        onClick={() => { setOpen(o => !o); if (!open) loadAlerts(); }}
        style={{
          position: "relative", background: t.surface2, border: `1px solid ${unread > 0 ? "#DC262644" : t.border}`,
          borderRadius: 8, padding: "7px 10px", cursor: "pointer", color: t.textSub,
          display: "flex", alignItems: "center", gap: 4,
          animation: unread > 0 ? "bell-glow 1.5s ease-in-out infinite" : "none",
        }}
        title="Alerts"
      >
        <span style={{
          fontSize: 15,
          display: "inline-block",
          transformOrigin: "top center",
          animation: unread > 0
            ? "bell-ring 2s ease-in-out infinite"
            : "bell-ring-gentle 5s ease-in-out infinite",
        }}>🔔</span>
        {unread > 0 && (
          <span style={{
            position: "absolute", top: -4, right: -4,
            background: "#DC2626", color: "#fff", borderRadius: "50%",
            width: 16, height: 16, fontSize: 9, fontWeight: 800,
            display: "flex", alignItems: "center", justifyContent: "center",
            border: `2px solid ${t.bg}`,
            animation: "badge-pulse 0.8s ease-in-out infinite",
            transformOrigin: "center",
          }}>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: "absolute", right: 0, top: "calc(100% + 8px)", zIndex: 1000,
          background: t.surface, border: `1px solid ${t.border}`, borderRadius: 16,
          boxShadow: "0 8px 32px #00000033", width: 360, maxHeight: 480,
          overflow: "hidden", display: "flex", flexDirection: "column",
          animation: "slideIn 0.18s ease",
        }}>
          <div style={{ padding: "14px 16px 10px", borderBottom: `1px solid ${t.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: t.text, fontFamily: "Georgia, serif" }}>
              Alerts {unread > 0 && <span style={{ fontSize: 10, background: "#DC262618", color: "#DC2626", border: "1px solid #DC262633", borderRadius: 20, padding: "1px 7px", marginLeft: 4 }}>{unread} new</span>}
            </span>
            {unread > 0 && (
              <button onClick={markAllRead} style={{ background: "none", border: "none", fontSize: 11, color: t.textMuted, cursor: "pointer", textDecoration: "underline" }}>
                Mark all read
              </button>
            )}
          </div>

          <div style={{ overflowY: "auto", flex: 1 }}>
            {loading && <div style={{ padding: "24px", textAlign: "center", color: t.textFaint, fontSize: 13 }}>Loading…</div>}
            {!loading && alerts.length === 0 && (
              <div style={{ padding: "32px 16px", textAlign: "center" }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>✅</div>
                <div style={{ fontSize: 13, color: t.textMuted }}>No alerts — everything looks normal.</div>
              </div>
            )}
            {!loading && alerts.map(a => (
              <div key={a.id}
                style={{
                  padding: "12px 16px", borderBottom: `1px solid ${t.border}`,
                  background: a.read ? "transparent" : `${SEVERITY_COLOR[a.severity] || "#2563EB"}08`,
                  cursor: "pointer", transition: "background 0.15s",
                }}
                onClick={() => !a.read && markRead(a.id)}
                onMouseEnter={e => e.currentTarget.style.background = t.surface2}
                onMouseLeave={e => e.currentTarget.style.background = a.read ? "transparent" : `${SEVERITY_COLOR[a.severity] || "#2563EB"}08`}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                      {!a.read && <div style={{ width: 6, height: 6, borderRadius: "50%", background: SEVERITY_COLOR[a.severity] || "#2563EB", flexShrink: 0 }} />}
                      <span style={{ fontSize: 11, fontWeight: a.read ? 600 : 800, color: a.read ? t.textSub : t.text, lineHeight: 1.4 }}>
                        {a.message}
                      </span>
                    </div>
                    <div style={{ fontSize: 10, color: t.textFaint }}>
                      {a.platform !== "all" ? `${a.platform} · ` : ""}
                      {new Date(a.createdAt).toLocaleString()}
                    </div>
                  </div>
                  <button
                    onClick={e => { e.stopPropagation(); dismiss(a.id); }}
                    style={{ background: "none", border: "none", color: t.textFaint, cursor: "pointer", fontSize: 14, padding: "0 2px", flexShrink: 0 }}
                  >×</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
